import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Notification type - extends beyond just errors
export type NotificationType = 'error' | 'success' | 'warning' | 'info';

// Notification item interface (renamed from ErrorItem for broader use)
export interface NotificationItem {
  id: string;
  message: string;
  title?: string;
  timestamp: number;
  type: NotificationType;
}

// Backward-compatible alias
export type ErrorItem = NotificationItem;

// Notification state interface
export interface ErrorState {
  errorQueue: NotificationItem[];
}

// Payload interface for showing notifications
export interface ShowErrorPayload {
  message: string;
  title?: string;
}

export interface ShowNotificationPayload {
  message: string;
  title?: string;
  type?: NotificationType;
}

// Initial state
const initialState: ErrorState = {
  errorQueue: [],
};

// Create the error/notification slice
const errorSlice = createSlice({
  name: 'error',
  initialState,
  reducers: {
    showError: (state, action: PayloadAction<ShowErrorPayload>) => {
      const { message, title } = action.payload;
      const errorItem: NotificationItem = {
        id: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        message,
        title: title || 'Error',
        timestamp: Date.now(),
        type: 'error',
      };
      
      state.errorQueue.push(errorItem);
      
      console.error(`[NotificationModal] ${errorItem.title}: ${errorItem.message}`, {
        id: errorItem.id,
        timestamp: errorItem.timestamp,
      });
    },
    showNotification: (state, action: PayloadAction<ShowNotificationPayload>) => {
      const { message, title, type = 'info' } = action.payload;
      const notificationItem: NotificationItem = {
        id: `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        message,
        title: title || (type === 'success' ? 'Success' : type === 'warning' ? 'Warning' : 'Info'),
        timestamp: Date.now(),
        type,
      };
      
      state.errorQueue.push(notificationItem);
      
      if (type === 'error') {
        console.error(`[NotificationModal] ${notificationItem.title}: ${notificationItem.message}`);
      } else {
        console.log(`[NotificationModal] ${notificationItem.title}: ${notificationItem.message}`);
      }
    },
    dismissError: (state) => {
      state.errorQueue.shift();
    },
    clearAllErrors: (state) => {
      state.errorQueue = [];
    },
  },
});

// Export actions
export const { showError, showNotification, dismissError, clearAllErrors } = errorSlice.actions;

// Export reducer
export default errorSlice.reducer;

// Selectors
export const selectCurrentError = (state: { error: ErrorState }): NotificationItem | null => 
  state.error.errorQueue.length > 0 ? state.error.errorQueue[0] : null;

export const selectHasErrors = (state: { error: ErrorState }): boolean => 
  state.error.errorQueue.length > 0;

export const selectErrorQueue = (state: { error: ErrorState }): NotificationItem[] => 
  state.error.errorQueue;

export const selectErrorCount = (state: { error: ErrorState }): number => 
  state.error.errorQueue.length;